import React, { useRef, useState, useEffect } from "react";
import styles from "./CopyWrapper.module.css";

type CopyMode = "text" | "html";

type CopyWrapperProps = {
  /** If provided, this string will be copied instead of reading from children. */
  copyValue?: string;
  /** If provided, called with the text that was copied. */
  onCopy?: (copiedText: string) => void;
  /** Whether to copy plain text (innerText) or HTML (innerHTML). Default: "text" */
  copyMode?: CopyMode;
  /** How long (ms) to show the "Copied" state. Default: 2000 */
  copiedDurationMs?: number;
  /** Optional className applied to the outer wrapper */
  className?: string;
  /** Optional aria-label for the copy button */
  copyButtonAriaLabel?: string;
  children?: React.ReactNode;
};

const CopyWrapper: React.FC<CopyWrapperProps> = ({
  copyValue,
  onCopy,
  copyMode = "text",
  copiedDurationMs = 2000,
  className = "",
  copyButtonAriaLabel,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), copiedDurationMs);
    return () => clearTimeout(t);
  }, [copied, copiedDurationMs]);

  const getTextToCopy = (): string => {
    if (copyValue !== undefined) return copyValue;
    const el = containerRef.current;
    if (!el) return "";

    return copyMode === "html" ? el.innerHTML : el.innerText;
  };

  const copy = async () => {
    const text = getTextToCopy();
    if (!text) return;

    // Try modern clipboard API first
    try {
      if (copyMode === "html" && (navigator as any).clipboard && (window as any).ClipboardItem) {
        // copy HTML and plain text as a ClipboardItem (best-effort, may not be supported everywhere)
        const blobPlain = new Blob([containerRef.current?.innerText ?? ""], { type: "text/plain" });
        const blobHtml = new Blob([containerRef.current?.innerHTML ?? ""], { type: "text/html" });
        const item: any = new ClipboardItem({
          "text/plain": blobPlain,
          "text/html": blobHtml,
        });
        await (navigator as any).clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(text);
      }
      setCopied(true);
      onCopy?.(text);
      return;
    } catch (e) {
      // fallback below
    }

    // Legacy fallback using textarea + execCommand
    try {
      if (copyMode === "html") {
        // For HTML fallback we'll copy innerText instead (since execCommand can't set html reliably)
        // That's a best-effort fallback
      }
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      onCopy?.(text);
    } catch (err) {
      // final fallback: do nothing (could surface toast/error)
      // console.warn("Copy failed", err);
    }
  };

  return (
    <div className={`${styles.wrapper} ${className}`}>
      <button
        type="button"
        className={`${styles.copyButton} ${copied ? styles.copied : ""}`}
        onClick={copy}
        aria-label={copyButtonAriaLabel ?? (copied ? "Copied to clipboard" : "Copy contents to clipboard")}
        title={copied ? "Copied!" : "Copy"}
      >
        {!copied ? (
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-1 14H9V8h9v11z" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
            <path d="M9 16.2l-3.5-3.5L4 14.2 9 19.2 20 8.2 18.6 6.8z" />
          </svg>
        )}

        Copy Text
      </button>

      <div className={styles.content} ref={containerRef} aria-live="off">
        {children}
      </div>

      {/* Screen-reader announcement */}
      <span className={styles.srOnly} aria-live="polite">
        {copied ? "Copied to clipboard" : ""}
      </span>
    </div>
  );
};

export default CopyWrapper;
